/**
 * Сводка по всему парку за период.
 *
 * Пункт 18. Отдельный экран, а не расширение истории: история отвечает на
 * вопрос «что делала эта машина», а сводка — «как отработал парк». Второй
 * вопрос задаёт руководитель, и ему нужны все строки разом, отсортированные
 * по пробегу.
 *
 * Запрос уходит по всем видимым машинам сразу. Порции по пятьдесят режет
 * сервер: предел стоит там не ради удобства, а чтобы неподъёмный запрос не
 * дошёл до базы, и обходить его из браузера нельзя.
 */

import { useEffect, useState } from 'react'

import { ApiError, api, type Device, type Position, type SummaryRow } from './api'
import { hoursDecimal, km, kmh, num } from './format'
import { dict, type Lang } from './i18n'
import { title } from './vehicleId'
import { downloadCsv, reportCsv, reportFilename } from './reportExport'
import { believable } from './summary'
import { fuelUsedLiters } from './vehicle'

type Range = '24h' | '7d' | '30d'

const HOURS: Record<Range, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 }

type Props = {
  devices: Device[]
  /**
   * Текущие позиции машин. Расход с установки (AVL 107) — накопительный
   * счётчик, и отчёт summary его не несёт. Все периоды кончаются «сейчас»,
   * поэтому последняя позиция периода и есть текущая.
   */
  positions: Map<number, Position>
  lang: Lang
}

export function FleetReport({ devices, positions, lang }: Props) {
  const t = dict(lang)
  const [range, setRange] = useState<Range>('7d')
  const [rows, setRows] = useState<SummaryRow[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const bounds = () => {
    const until = new Date()
    return { since: new Date(until.getTime() - HOURS[range] * 3600_000), until }
  }

  useEffect(() => {
    if (devices.length === 0) {
      setRows([])
      return
    }
    let cancelled = false
    setBusy(true)
    setError('')
    const until = new Date()
    const since = new Date(until.getTime() - HOURS[range] * 3600_000)
    api
      .report<SummaryRow[]>('summary', devices.map((d) => d.id), since, until)
      .then((data) => {
        if (!cancelled) setRows(data)
      })
      .catch((caught) => {
        if (cancelled) return
        setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
        setRows(null)
      })
      .finally(() => {
        if (!cancelled) setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [devices, range, t.networkError])

  // По госномеру, а не по имени из Traccar: в списке машин человек видит
  // именно номер, и отчёт обязан звать машину так же. Выгрузка в CSV идёт
  // тем же именем — иначе таблицу не сопоставить с экраном.
  const nameById = new Map(devices.map((d) => [d.id, title(d)]))
  // По пробегу вниз: первым идёт тот, кто больше всех отработал.
  const ordered = [...(rows ?? [])].sort((a, b) => b.distance - a.distance)

  /** Выгружается ровно то, что на экране: тот же порядок, тот же период. */
  function save() {
    const { since, until } = bounds()
    downloadCsv(
      reportFilename('summary', since, until),
      reportCsv('summary', ordered, { lang, nameById, positions }),
    )
  }
  const totalDistance = ordered.reduce((sum, row) => sum + row.distance, 0)
  // Итог по моточасам показываем, только если посчитаны все машины. Сумма, в
  // которой часть слагаемых пропущена, хуже прочерка: она выглядит как ответ,
  // но им не является.
  const hoursKnown = ordered.every((row) => believable(row, 'engineHours'))
  const totalHours = ordered.reduce((sum, row) => sum + row.engineHours, 0)

  return (
    <div className="history">
      <div className="chips" role="group" aria-label={t.period}>
        {(['24h', '7d', '30d'] as const).map((key) => (
          <button key={key} aria-pressed={range === key} onClick={() => setRange(key)}>
            {key === '24h' ? t.last24h : key === '7d' ? t.last7d : t.last30d}
          </button>
        ))}
      </div>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      {busy && !error && <p className="hint">{t.loading}</p>}

      {!busy && !error && ordered.length === 0 && <p className="hint">{t.nothingFound}</p>}

      {!busy && !error && ordered.length > 0 && (
        <>
          <div className="rec">
            <div className="rec-top">
              <b>{t.fleetTotal}</b>
              <span>
                {km(totalDistance, lang, false)} {t.unitKm}
              </span>
            </div>
            <div className="rec-sub">
              {t.engineHours}:{' '}
              {hoursKnown ? `${hoursDecimal(totalHours, lang)} ${t.unitHour}` : '—'}
            </div>
          </div>

          <div className="rows">
            {ordered.map((row) => (
              <div className="rec" key={row.deviceId}>
                <div className="rec-top">
                  <b>{nameById.get(row.deviceId) ?? row.deviceName ?? `#${row.deviceId}`}</b>
                  <span>
                    {km(row.distance, lang, false)} {t.unitKm}
                  </span>
                </div>
                <div className="rec-sub">
                  {t.engineHours}:{' '}
                  {believable(row, 'engineHours')
                    ? `${hoursDecimal(row.engineHours, lang)} ${t.unitHour}`
                    : '—'}{' '}
                  · {t.avgSpeed}{' '}
                  {believable(row, 'averageSpeed') ? kmh(row.averageSpeed, lang) : '—'} ·{' '}
                  {t.maxSpeed}{' '}
                  {believable(row, 'maxSpeed') ? `${kmh(row.maxSpeed, lang)} ${t.speed}` : '—'}
                </div>
                <FuelUsedLine liters={fuelUsedLiters(positions.get(row.deviceId))} lang={lang} />
              </div>
            ))}
          </div>

          <button className="act ghost" onClick={save}>
            {t.exportAction}
          </button>
        </>
      )}
    </div>
  )
}

/**
 * Сколько сожжено с установки трекера — не за период, это видно по подписи.
 * Итога по парку нет намеренно: у счётчиков разные даты установки, и сумма
 * ничего не значит. Машина без шины CAN строки не получает вовсе.
 */
function FuelUsedLine({ liters, lang }: { liters: number | null; lang: Lang }) {
  if (liters === null) return null
  const t = dict(lang)
  return (
    <div className="rec-sub">
      {t.fuelUsedSinceInstall}: {num(liters, lang)} {t.unitLiter}
    </div>
  )
}
